/**
 * ModernWash 미러 화면 순수 헬퍼 — 상태→문구 매핑, "0 단정 금지" 규칙, 기간 변환의 단일 소스.
 *
 * 왜 순수 함수로 빼는가:
 *   "동기화 전 / 원천 데이터 없음 / 동기화 실패(마지막 성공 노출) / 오래된 데이터" 는 서로
 *   다른 의미인데 화면이 전부 "0" 으로 뭉개면 운영자가 "장사가 안 됐다"와 "아직 안 받아왔다"를
 *   구분할 수 없다. 그 판정 규칙을 여기 한 곳에 모아 테스트로 고정한다.
 *
 * 백엔드 계약: backend/src/modernwash-legacy-mirror/dto/modernwash-legacy-mirror.dto.ts
 *   FreshnessStatus  = never_synced | fresh | stale | showing_last_success
 *   SectionDataState = never_synced | ok | no_source_data | failed_showing_last_success | stale
 */

// ── 서비스 라벨 ────────────────────────────────────────────────
export const SERVICE_OPTIONS = [
  { key: 'mogoo', label: 'Mogoo 전체' }, // 네이티브(모구)만 — 기본값
  { key: 'modernwash', label: 'ModernWash 운영' }, // 레거시 미러만
];

export const serviceLabel = (service) =>
  SERVICE_OPTIONS.find((o) => o.key === service)?.label ?? 'Mogoo 전체';

// ── 전체 신선도 배너 문구 ──────────────────────────────────────
// tone 은 uiTokens 의 STATUS_COLORS 키(success/warning/danger/neutral/info)와 맞춘다.
export const FRESHNESS_META = {
  fresh: {
    tone: 'success',
    label: '최신',
    desc: '최근 동기화가 성공했습니다.',
  },
  stale: {
    tone: 'warning',
    label: '오래된 데이터',
    desc: '마지막 성공 이후 시간이 지났습니다. 최신이 아닐 수 있어요.',
  },
  showing_last_success: {
    tone: 'warning',
    label: '마지막 성공 데이터 표시 중',
    desc: '최근 동기화가 실패해 마지막으로 성공한 스냅샷을 보여줍니다.',
  },
  never_synced: {
    tone: 'neutral',
    label: '동기화 전',
    desc: '아직 성공한 동기화가 없습니다. 원천 준비 중입니다.',
  },
};

export const freshnessMeta = (status) => FRESHNESS_META[status] ?? FRESHNESS_META.never_synced;

// ── 섹션(프로젝션)별 데이터 상태 문구 ─────────────────────────
// placeholder=true 면 "숫자를 단정하면 안 되는" 상태 → 값 자리에 문구를 넣는다(0 금지).
export const SECTION_STATE_META = {
  ok: { tone: 'success', label: null, placeholder: false, note: null },
  never_synced: {
    tone: 'neutral',
    label: '동기화 전',
    placeholder: true,
    note: '원천 준비중',
  },
  no_source_data: {
    // 마지막 성공 동기화가 원천에서 0건을 받음 = "진짜 0". 이때만 0 을 써도 된다.
    tone: 'neutral',
    label: '원천 데이터 없음',
    placeholder: false,
    note: '기간 내 기록 0건',
  },
  failed_showing_last_success: {
    tone: 'warning',
    label: '마지막 성공 데이터',
    placeholder: false,
    note: '최근 동기화 실패',
  },
  stale: {
    tone: 'warning',
    label: '오래된 데이터',
    placeholder: false,
    note: '최신 동기화 지연',
  },
};

export const sectionStateMeta = (state) => SECTION_STATE_META[state] ?? SECTION_STATE_META.never_synced;

/**
 * KPI 값 렌더 규칙 — **오래됐거나 없거나 동기화 전인 값을 절대 0 으로 단정하지 않는다.**
 *
 * 반환: { text, placeholder, badge }
 *   - never_synced           → text="동기화 전"  (placeholder)  ※ 숫자 아님
 *   - 값이 null/undefined
 *       · no_source_data     → text="0{unit}"    (진짜 0 — 허용)
 *       · 그 외              → text="—"          (placeholder)  ※ 0 아님
 *   - 값이 숫자
 *       · stale/failed       → text=값 + 경고 badge (마지막 성공값 유지)
 *       · ok                 → text=값
 *
 * @param {number|null|undefined} value  백엔드 카운트(없으면 null)
 * @param {string} sectionState          SectionDataState
 * @param {string} unit                  단위 접미사(예: '건', '명')
 */
export function renderMetricValue(value, sectionState, unit = '') {
  const meta = sectionStateMeta(sectionState);
  const withUnit = (n) => `${Number(n).toLocaleString('ko-KR')}${unit}`;

  if (sectionState === 'never_synced') {
    return { text: meta.label, placeholder: true, badge: meta };
  }
  if (value === null || value === undefined) {
    if (sectionState === 'no_source_data') {
      return { text: `0${unit}`, placeholder: false, badge: meta };
    }
    // 값이 없는데 never_synced 도 아니고 no_source_data 도 아니면 0 이라 단정하지 않는다.
    return { text: '—', placeholder: true, badge: meta };
  }
  return { text: withUnit(value), placeholder: false, badge: meta };
}

// ── 카테고리 라벨 ──────────────────────────────────────────────
/** taxonomy 응답에서 선택 서비스의 카테고리 목록을 뽑는다(없으면 []). */
export function categoriesForService(taxonomy, service) {
  const services = taxonomy?.services ?? [];
  const entry = services.find((s) => s.service === service) ?? services[0];
  return entry?.categories ?? [];
}

/** 카테고리 key → 라벨. 빈 값이면 서비스별 "전체" 라벨. */
export function categoryLabel(taxonomy, service, categoryKey) {
  if (!categoryKey) return service === 'mogoo' ? '전체 카테고리' : '전체';
  const found = categoriesForService(taxonomy, service).find((c) => c.key === categoryKey);
  return found?.label ?? categoryKey;
}

// ── 기간(period) ───────────────────────────────────────────────
// UI 기간 모드: today | thisMonth | month | range
export const PERIOD_OPTIONS = [
  { key: 'today', label: '오늘' },
  { key: 'thisMonth', label: '이번 달' },
  { key: 'month', label: '월 선택' },
  { key: 'range', label: '기간 지정' },
];
export const PERIOD_KEYS = PERIOD_OPTIONS.map((o) => o.key);
export const DEFAULT_PERIOD = 'thisMonth'; // 과제 기본값

const YM_RE = /^\d{4}-\d{2}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const pad2 = (n) => String(n).padStart(2, '0');

/** now 기준 현재 월 YYYY-MM. */
export function currentYm(now = new Date()) {
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
}

/** YYYY-MM → { start:'YYYY-MM-01', end:'YYYY-MM-<말일>' }. */
export function monthBounds(ym) {
  const [y, m] = ym.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate(); // m 은 1-based → new Date(y, m, 0)=그 달 말일
  return { start: `${ym}-01`, end: `${ym}-${pad2(lastDay)}` };
}

/**
 * UI 기간 상태 → 백엔드 쿼리 파라미터.
 * 백엔드는 today|7d|30d|custom 만 받으므로 월/범위는 custom(start,end)로 변환한다.
 *   today     → { period:'today' }
 *   thisMonth → { period:'custom', start,end = 이번 달 경계 }
 *   month     → { period:'custom', start,end = 선택 월 경계 }
 *   range     → { period:'custom', start,end }  (불완전하면 이번 달로 폴백)
 */
export function toBackendPeriod(state = {}, now = new Date()) {
  const { period = DEFAULT_PERIOD, month, start, end } = state;
  if (period === 'today') return { period: 'today' };
  if (period === 'range') {
    if (DATE_RE.test(start || '') && DATE_RE.test(end || '') && start <= end) {
      return { period: 'custom', start, end };
    }
    const b = monthBounds(currentYm(now)); // 불완전 범위 폴백
    return { period: 'custom', start: b.start, end: b.end };
  }
  const ym = period === 'month' && YM_RE.test(month || '') ? month : currentYm(now);
  const b = monthBounds(ym);
  return { period: 'custom', start: b.start, end: b.end };
}

/** 스코프 칩에 붙일 기간 라벨. */
export function periodLabel(state = {}, now = new Date()) {
  const { period = DEFAULT_PERIOD, month, start, end } = state;
  if (period === 'today') return '오늘';
  if (period === 'range') {
    if (DATE_RE.test(start || '') && DATE_RE.test(end || '')) {
      return `${fmtDot(start)} ~ ${fmtDot(end)}`;
    }
    return '기간 지정';
  }
  const ym = period === 'month' && YM_RE.test(month || '') ? month : currentYm(now);
  const [y, m] = ym.split('-');
  return `${y}년 ${Number(m)}월`;
}

/** 'YYYY-MM-DD' → 'YYYY. M. D.' (ko-KR). */
function fmtDot(dateStr) {
  if (!DATE_RE.test(dateStr || '')) return dateStr || '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('ko-KR');
}

/**
 * KPI 카드/섹션 상단 스코프 라벨. 예) "ModernWash 운영 · 실제 세차 · 2026년 8월"
 * 매장명은 넣지 않는다(네이티브 stats 는 매장 스코프가 아니라서 카드마다 오해를 부른다).
 */
export function scopeLabel({ service, categoryText, periodState }, now = new Date()) {
  return `${serviceLabel(service)} · ${categoryText} · ${periodLabel(periodState, now)}`;
}
